import { InfantDto, Config, VisitDto, UserInfoDto } from '@ecdlink/core';
import {
  InfantModelInput,
  MotherChildVisitModelInput,
  VisitModelInput,
} from '@ecdlink/graphql';
import { api } from '../axios.helper';
import { VisitService } from '../VisitService';
class InfantService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  async GetAllInfantsForMother(
    visitType?: 'all' | 'due'
  ): Promise<InfantDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'getAllInfantsForHealthCareWorker',
      variables: {
        visitType,
      },
    });

    if (response.status !== 200) {
      throw new Error('Getting infants failed - Server connection error');
    }
    return response.data.data.allInfantsForHealthCareWorker;
  }

  async getInfantStatusInfoForIds(
    infantUserIds: string[]
  ): Promise<UserInfoDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'getInfantStatusInfoForIds',
      variables: {
        infantUserIds,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Getting getInfantStatusInfoForIds failed - Server connection error'
      );
    }

    return response.data.data.infantStatusInfoForIds;
  }

  async addInfant(
    input: InfantModelInput,
    clientVisits: MotherChildVisitModelInput[]
  ): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'addInfant',
      variables: {
        input: input,
        clientVisits: clientVisits,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating mother failed - Server connection error');
    }

    return response.data.data.createInfant;
  }

  async updateInfant(id: string, input: InfantModelInput): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateInfant: VisitDto };
    }>(``, {
      id: 'updateInfant',
      variables: {
        input,
        id,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating infant failed - Server connection error');
    }

    return response.data.data.updateInfant;
  }

  async GetInfantVisits(id: string): Promise<VisitDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { infantVisits: VisitDto[] };
    }>(``, {
      id: 'GetInfantVisits',
      variables: {
        userId: id,
      },
    });

    if (response.status !== 200) {
      throw new Error('Getting Infant visits failed - Server connection error');
    }

    return response.data.data.infantVisits;
  }

  async getAllVisitsForInfantIds(infantUserIds: string[]): Promise<VisitDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { allVisitsForInfantIds: VisitDto[] };
    }>(``, {
      id: 'getAllVisitsForInfantIds',
      variables: {
        infantUserIds: infantUserIds,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Getting allVisitsForInfantIds failed - Server connection error'
      );
    }

    return response.data.data.allVisitsForInfantIds;
  }

  async updateInfantCaregiverAddress(
    id: string,
    input: InfantModelInput
  ): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateInfantCaregiverAddress',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Updating infant caregiver address failed - Server connection error'
      );
    }

    return response.data.data.updateInfantCaregiverAddress;
  }

  async updateInfantCaregiverContactDetails(
    id: string,
    input: InfantModelInput
  ): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateInfantCaregiverContactDetails',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Updating infant caregiver contact details failed - Server connection error'
      );
    }

    return response.data.data.updateInfantCaregiverContactDetails;
  }

  async updateInfantCaregiver(
    infantId: string,
    input: InfantModelInput
  ): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateInfantCaregiver: InfantDto };
      errors?: {};
    }>(``, {
      id: 'UpdateInfantCaregiver',
      variables: {
        infantId,
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error('Update Infant Caregiver - Server connection error');
    }

    return response.data.data.updateInfantCaregiver;
  }

  async addAdditionalVisitForChild(input: VisitModelInput): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addAdditionalVisitForInfant: VisitService };
      errors?: {};
    }>(``, {
      id: 'AddAdditionalVisitForInfant',
      variables: {
        input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'add Additional Visit For Child failed - Server connection error'
      );
    }

    return response.data.data.addAdditionalVisitForInfant;
  }

  async syncInfantVisits(clientVisits: VisitDto[]): Promise<InfantDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'syncInfantVisits',
      variables: {
        clientVisits,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Syncing infants visits failed - Server connection error'
      );
    }

    return response.data.data.syncInfantVisits;
  }
}

export default InfantService;
